import Events from 'src/logging/Events';
import AVLiveApi, {HardwareTypes} from 'src/api/AVLiveApi';
import PlatformApi from 'src/api/PlatformApi';
import AppApi from 'src/api/AppApi';
import AccountApi from 'src/api/AccountApi';
import VendorsExchangeDevice from 'src/services/bluetooth/vendorsExchange/VendorsExchangeDevice';
import AppDispatcher from 'src/dispatchers/AppDispatcher';
import AccountStore from 'src/stores/AccountStore';
import type {VendingMachine} from 'src/services/bluetooth/VendingMachineInterface';
import MachineConstants from 'src/constants/MachineConstants';
import AccountConstants from 'src/constants/AccountConstants';
import GmaApi from 'src/api/GmaApi';
import Settings from 'src/Settings';
import Logger from 'src/logging/Logger';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import ActionsFactory from 'src/actions/ActionsFactory';
import {store} from 'src/redux/store';
import {
  addLinkedLocationToRecents,
  setSelectedLocation,
} from 'src/redux/slices/campusLocationSlice';

class MachineActions {
  clearMachines() {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.CLEAR_MACHINES,
    });
  }

  clearMarkets() {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.CLEAR_MARKETS,
    });
  }

  async machineAdded(machine: VendingMachine) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.MACHINE_ADDED,
      data: {
        machine,
      },
    });
    Events.BluetoothDeviceScanned.trackEvent('machine', machine.deviceId);
    let gmaId = '';
    const locationId = AccountStore.getLocationId();

    if (!locationId) {
      gmaId = AccountStore.getAccountId();
    }

    try {
      const response = await AVLiveApi.getMachineInfo(machine.deviceId, gmaId);
      Events.BluetoothDeviceFound.trackEvent(
        'machine',
        machine.deviceId,
        response.Name,
      );
      let machineAddress = null;
      if (response?.LocationId && Settings.isNewUI()) {
        machineAddress = await this.getMachineLocationAddress(
          response?.LocationId,
        );
      }
      AppDispatcher.handleViewAction({
        actionType: MachineConstants.MACHINE_UPDATED,
        data: {
          machine: {...response, DeviceId: machine.deviceId},
          address: machineAddress,
        },
      });
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'MachineActions:MachineAdded',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'MachineActions:MachineAdded',
        generateErrorMessage(error),
      );
    }
  }

  async getMachineLocationAddress(locationId: string) {
    const machineLocation = await PlatformApi.fetchLocation(locationId);
    return {
      address: machineLocation.address,
      city: machineLocation.city,
      state: machineLocation.state,
      zip: machineLocation.zip,
      country: machineLocation.country,
    };
  }

  clearDevices() {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.CLEAR_DEVICES,
      data: {},
    });
  }

  errorConnecting() {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.ERROR_CONNECTING,
      data: {
        error: 'general',
      },
    });
  }

  machineNotAvailable(isOperator: boolean, deviceId: string) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.ERROR_CONNECTING,
      data: {
        error: 'notavailable',
      },
    });

    if (!isOperator) {
      AVLiveApi.sendEmail(
        deviceId,
        'AirVend Inside Mdb Communication Down',
        '',
        '',
        'This machine has lost communication with the AirVend Inside Device (MDB)',
      );
    }
  }

  invalidKey() {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.ERROR_CONNECTING,
      data: {
        error: 'invalidkey',
      },
    });
  }

  async deviceAddedVE(device: VendorsExchangeDevice) {
    Events.BluetoothDeviceScanned.trackEvent('VE Device', device.deviceId);
    const response: any = await AVLiveApi.getMachineInfoV3(
      device.deviceId,
      AccountStore.getAccountId(),
      HardwareTypes.VEX,
    );

    if (response) {
      Events.BluetoothDeviceFound.trackEvent(
        'VE Device',
        device.deviceId,
        response.Name,
      );

      if (response.Name) {
        device.setDeviceName(response.Name);
        device.setImageUrl(response.ImageUrl);
        AppDispatcher.handleViewAction({
          actionType: MachineConstants.MACHINE_ADDED,
          data: {
            machine: device,
          },
        });
      } else {
        AppDispatcher.handleViewAction({
          actionType: MachineConstants.DEVICE_ADDED,
          data: {
            device,
          },
        });
      }
    }
  }

  async deviceAdded(device: VendingMachine) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.DEVICE_ADDED,
      data: {
        device,
      },
    });
    Events.BluetoothDeviceScanned.trackEvent('device', device.deviceId);
    let gmaId = '';
    const locationId = AccountStore.getLocationId();

    if (!locationId) {
      gmaId = AccountStore.getAccountId();
    }

    try {
      const response = await AVLiveApi.getMachineInfo(device.deviceId, gmaId);
      Events.BluetoothDeviceFound.trackEvent(
        'device',
        device.deviceId,
        response.Name,
      );
      let machineDeviceAddress = null;
      if (Settings.isNewUI() && response?.LocationId) {
        machineDeviceAddress = await this.getMachineLocationAddress(
          response?.LocationId,
        );
      }
      AppDispatcher.handleViewAction({
        actionType: MachineConstants.DEVICE_UPDATED,
        data: {
          address: machineDeviceAddress,
          machine: {...response, DeviceId: device.deviceId},
        },
      });
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'Machine:DeviceAdded',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'Machine:DeviceAdded',
        generateErrorMessage(error),
      );
    }
  }

  async handleServiceComplete(deviceId: string, data: any) {
    try {
      const response = await AVLiveApi.serviceComplete(
        deviceId,
        data.date,
        data.cashBagAmount,
      );
      AppDispatcher.handleViewAction({
        actionType: MachineConstants.SERVICE_COMPLETE,
        data: {
          response: response,
          deviceId: deviceId,
        },
      });
    } catch (error) {
      AppDispatcher.handleViewAction({
        actionType: MachineConstants.SERVICE_COMPLETE_ERROR,
        data: {
          error: error,
          deviceId: deviceId,
        },
      });
    }
  }

  dexDataReceived(message: string) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.DEX_DATA_RECEIVED,
      data: {
        dexDataReceived: message,
      },
    });
  }

  dexFailed(message: string) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.DEX_FAILED,
      data: {
        dexFailed: message,
      },
    });
  }

  deviceUnattached(deviceId: string) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.DEVICE_UNATTACHED,
      data: {
        deviceId: deviceId,
      },
    });
  }

  setupDataReceived(deviceId: string, setupData: any) {
    // update the store with the data from the device immediately
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.UPLOAD_SETUP_ERROR,
      data: {
        deviceId: deviceId,
        setupData: setupData,
      },
    });
  }

  async checkForUpdate(deviceId: string, version: string) {
    const payload = await AVLiveApi.checkVersion(deviceId, version);

    AppDispatcher.handleViewAction({
      actionType: MachineConstants.CHECK_VERSION,
      data: {
        payload,
      },
    });
  }

  uploadSetupData(deviceId: string, setupData: object): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await AVLiveApi.setup(deviceId, setupData);

        if (response.MachineConfig.MachineId > 0) {
          resolve(response);
        } else {
          this.deviceUnattached(deviceId);
          reject();
        }

        AppDispatcher.handleViewAction({
          actionType: MachineConstants.UPLOAD_SETUP,
          data: {
            response,
            deviceId,
            setupData,
          },
        });
      } catch (error) {
        reject(error);
        CrashlyticsEvents.log(
          'Exception',
          'MachineActions:UploadSetupData',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'MachineActions:UploadSetupData',
          generateErrorMessage(error),
        );
        AppDispatcher.handleViewAction({
          actionType: MachineConstants.UPLOAD_SETUP_ERROR,
          data: {
            error,
            deviceId,
            setupData,
          },
        });
      }
    });
  }

  uploadDexFiles(deviceId: string, files: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await AVLiveApi.uploadDexFiles(deviceId, files);
        resolve(response);
        AppDispatcher.handleViewAction({
          actionType: MachineConstants.UPLOAD_DEX_FILES,
          data: {
            response: response,
            deviceId: deviceId,
            files: files,
          },
        });
      } catch (error) {
        reject(error);
        AppDispatcher.handleViewAction({
          actionType: MachineConstants.UPLOAD_DEX_FILES_ERROR,
          data: {
            error: error,
            deviceId: deviceId,
            files: files,
          },
        });
      }
    });
  }

  sendEmail(
    deviceId: string,
    subject: string,
    name: string,
    email: string,
    comments: string,
  ) {
    AVLiveApi.sendEmail(deviceId, subject, name, email, comments); // We don't care about the response for now.
  }

  machineRebooting(reason: string) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.MACHINE_REBOOTING,
      data: {
        reason: reason,
      },
    });
  }

  disconnectedFromMachine() {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.DISCONNECT_FROM_MACHINE,
    });
  }

  async addMachineImage(deviceId: string, imageData: string, date: string) {
    const response = await AVLiveApi.addMachineImage(deviceId, imageData, date);

    if (response) {
      this.notifyImageUpdate(deviceId, response);
    }

    return response;
  }

  machineImageAddedOffline(deviceId: string, base64Image: string) {
    const imageUrl = 'data:image/jpeg;base64,' + base64Image;
    this.notifyImageUpdate(deviceId, imageUrl);
  }

  machineImageDeletedOffline(deviceId: string) {
    this.notifyImageUpdate(deviceId, null);
  }

  notifyImageUpdate(deviceId: string, imageUrl: string | null) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.MACHINE_IMAGE_UPDATED,
      data: {
        imageUrl,
        deviceId,
      },
    });
  }

  async deleteMachineImage(deviceId: string, date: string) {
    const response = await AVLiveApi.deleteMachineImage(deviceId, date);

    if (response) {
      this.notifyImageUpdate(deviceId, null);
    }

    return response;
  }

  async marketScanned(
    marketId: string,
    gmaId: string,
    type: string,
    distance = 0,
  ) {
    Events.BluetoothDeviceScanned.trackEvent(type, marketId, distance);

    if (AccountStore.isDemo()) {
      const response = {
        status: 'ok',
        msg: 'success',
        data: {
          id: 'e36b2b54c6ff0708b293c40d43dd7f50',
          org: 'a241a2924c6d6096522c51d1583778b0',
          type: 'sos',
          name: 'LocationA',
          city: 'Santa Clara',
          state: 'CA',
          timezone: 'America/Los_Angeles',
          startcardbal: 0,
          sitecode: 0,
          flag1: 0,
          nbr: 0,
          isdisabled: 'N',
          country: 'USA',
          broadcastid: '323334353637',
        },
      };
      AppDispatcher.handleViewAction({
        actionType: MachineConstants.MARKET_UPDATED,
        data: {
          response,
        },
      });
      return Promise.resolve(response);
    }

    AppDispatcher.handleViewAction({
      actionType: MachineConstants.MARKET_SCANNED,
      data: {
        market: {
          deviceId: marketId,
          distance,
        },
      },
    });

    const deviceResponse = await AccountApi.beaconLookUp(marketId, gmaId);
    Logger.Log.LogAPIEvent(
      'AccountAPI',
      'BeaconLookUp',
      JSON.stringify({marketId, gmaId}),
      JSON.stringify(deviceResponse),
    );

    let locationResponse: any;
    let paymentResponse: any;
    let canUserMakePurchaseResponse: any;
    if (deviceResponse && deviceResponse?.locationId) {
      locationResponse = await PlatformApi.fetchLocation(
        deviceResponse?.locationId,
      );
      Logger.Log.LogAPIEvent(
        'PlatformAPI',
        'FetchLocation',
        JSON.stringify({locationId: deviceResponse?.locationId}),
        JSON.stringify(locationResponse),
      );
      // If the account doesn't have a location yet, link it to this beacon's location
      const currentLocationId = AccountStore.getLocationId();

      if (!currentLocationId && deviceResponse?.locationId) {
        const linkResponse =
          await ActionsFactory.getAccountActions().linkLocation(
            gmaId,
            deviceResponse.locationId,
          );
        if (linkResponse?.success) {
          // Dispatch LOCATION_AUTO_LINKED with full location data
          AppDispatcher.handleViewAction({
            actionType: MachineConstants.LOCATION_AUTO_LINKED,
            data: {
              locationId: deviceResponse.locationId,
            },
          });

          // Also update the AccountStore with full location details
          AppDispatcher.handleViewAction({
            actionType: AccountConstants.LOCATION_LINK_ADDED,
            data: {
              id: locationResponse.locationId,
              locationId: locationResponse.locationId,
              name: locationResponse.name,
              address: locationResponse.address,
              city: locationResponse.city,
              state: locationResponse.state,
              orgId: locationResponse.orgId,
              orgName: locationResponse.orgName,
              broadcastId: (deviceResponse as any).broadcastId || '',
              beaconId: (deviceResponse as any).broadcastId || '',
              locationType: 'sos',
              orderAhead: true,
              localType: 'market',
            } as any,
          });

          // Fetch full location details for ordering capabilities
          try {
            const fullLocation = await PlatformApi.fetchLocation(
              locationResponse.locationId,
            );

            // Manually add location to recents list with full details
            store.dispatch(
              addLinkedLocationToRecents({
                locationId: fullLocation.locationId,
                name: fullLocation.name,
                displayName: fullLocation.displayName || fullLocation.name,
                city: fullLocation.city || '',
                state: fullLocation.state || '',
                campusId: null,
                lastUpdated: new Date().toISOString(),
              }),
            );

            // Also set it as the selected location with full details for ordering
            store.dispatch(setSelectedLocation(fullLocation));
          } catch (error) {
            CrashlyticsEvents.log(
              'Exception',
              'MachineActions:fetchFullLocationDetails',
              generateErrorMessage(error),
            );
            Events.Error.trackEvent(
              'Exception',
              'MachineActions:fetchFullLocationDetails',
              generateErrorMessage(error),
            );
            // Fallback to basic info
            store.dispatch(
              addLinkedLocationToRecents({
                locationId: locationResponse.locationId,
                name: locationResponse.name,
                displayName: locationResponse.name,
                city: locationResponse.city,
                state: locationResponse.state,
                campusId: null,
                lastUpdated: new Date().toISOString(),
              }),
            );
          }
        }
      }

      paymentResponse = await AppApi.getPaymentCredentials(gmaId);
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'GetPaymentCredentials',
        JSON.stringify({accountId: gmaId}),
        JSON.stringify(paymentResponse),
      );
      canUserMakePurchaseResponse = await AppApi.canUserMakePurchase(
        gmaId,
        deviceResponse?.orgId,
      );
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'CanUserMakePurchase',
        JSON.stringify({
          accountId: gmaId,
          orgId: deviceResponse?.orgId,
        }),
        JSON.stringify(canUserMakePurchaseResponse),
      );

      if (locationResponse && locationResponse?.locationId) {
        Events.BluetoothDeviceFound.trackEvent(
          'beacon',
          locationResponse.locationId,
          locationResponse.name,
        );
      }
    } else {
      CrashlyticsEvents.log(
        'Warning',
        'MachineActions:marketScanned',
        'No locationId in deviceResponse',
        JSON.stringify(deviceResponse),
      );
    }

    AppDispatcher.handleViewAction({
      actionType: MachineConstants.MARKET_UPDATED,
      data: {
        response: {
          ...locationResponse,
          ...deviceResponse,
          processorType: paymentResponse?.type,
          canUserMakePurchaseAtOrg:
            canUserMakePurchaseResponse?.canUserMakePurchase,
          address: locationResponse?.address,
          orgId: locationResponse?.orgId,
          localization: {
            currency: deviceResponse.currency,
          },
        },
        distance,
      },
    });
    return {
      ...deviceResponse,
      ...locationResponse,
      processorType: paymentResponse?.type,
      canUserMakePurchaseAtOrg:
        canUserMakePurchaseResponse?.canUserMakePurchase,
    };
  }

  // It will be removed once new API is ready
  // Used for Canteen Pantry create account flow
  async marketScannedLegacy(
    marketId: string,
    gmaId: string,
    type: string,
    distance = 0,
  ) {
    Events.BluetoothDeviceScanned.trackEvent(type, marketId, distance);

    if (AccountStore.isDemo()) {
      const response = {
        status: 'ok',
        msg: 'success',
        data: {
          id: 'e36b2b54c6ff0708b293c40d43dd7f50',
          org: 'a241a2924c6d6096522c51d1583778b0',
          type: 'sos',
          name: 'LocationA',
          city: 'Santa Clara',
          state: 'CA',
          timezone: 'America/Los_Angeles',
          startcardbal: 0,
          sitecode: 0,
          flag1: 0,
          nbr: 0,
          isdisabled: 'N',
          country: 'USA',
          broadcastid: '323334353637',
        },
      };
      AppDispatcher.handleViewAction({
        actionType: MachineConstants.MARKET_UPDATED_LEGACY,
        data: {
          response,
        },
      });
      return Promise.resolve(response);
    }

    AppDispatcher.handleViewAction({
      actionType: MachineConstants.MARKET_SCANNED,
      data: {
        market: {
          deviceId: marketId,
          distance,
        },
      },
    });

    const response = await GmaApi.getAccountFromScannedId(
      marketId,
      gmaId,
      type,
    );

    Events.BluetoothDeviceFound.trackEvent(
      'beacon',
      response.locationId,
      response.locationId,
    );

    AppDispatcher.handleViewAction({
      actionType: MachineConstants.MARKET_UPDATED_LEGACY,
      data: {
        response,
        distance,
      },
    });
    return response;
  }

  uploadVmcLog(deviceId: string, logs: string) {
    return AVLiveApi.uploadVmcLog(deviceId, logs);
  }

  vmcLogReceived(deviceId: string, logs: any) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.VMC_LOG_RECEIVED,
      data: {
        logs,
        deviceId,
      },
    });
  }

  serviceModeChanged(serviceMode: boolean) {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.SERVICE_MODE_CHANGED,
      data: {
        serviceMode,
      },
    });
  }

  machineConnected() {
    AppDispatcher.handleViewAction({
      actionType: MachineConstants.MACHINE_CONNECTED,
    });
  }

  uploadMDBOptions(deviceId: string, mdbOptions: any) {
    return AVLiveApi.uploadMDBOptions(deviceId, mdbOptions);
  }
}

export default new MachineActions();
